import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MainNavigation } from '../navigation/MainNavigation';
import {
  BarChart3,
  Users,
  Settings,
  Shield,
  Home,
  FileText
} from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ 
  children, 
  title, 
  subtitle 
}) => {
  const location = useLocation();

  const adminLinks = [
    {
      to: '/admin/dashboard',
      icon: BarChart3,
      label: 'Dashboard',
      description: 'System overview and metrics'
    },
    {
      to: '/admin/profiles',
      icon: Users,
      label: 'User Profiles',
      description: 'Manage user accounts and data'
    },
    {
      to: '/admin/config',
      icon: Settings,
      label: 'Configuration',
      description: 'Environment and service settings'
    },
    {
      to: '/admin/pdf-monitoring',
      icon: FileText,
      label: 'PDF Monitoring',
      description: 'Monitor PDF generation queue and worker performance'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <MainNavigation variant="unified" />
      
      <div className="container mx-auto px-4 py-8">
        {/* Admin Header with Navigation */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex mb-4 text-sm text-gray-600">
            <Link to="/" className="hover:text-gray-900 flex items-center gap-1">
              <Home className="w-4 h-4" />
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-400">Admin</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">
              {adminLinks.find(link => link.to === location.pathname)?.label || 'Page'}
            </span>
          </nav>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
              {subtitle && (
                <p className="text-gray-600 mt-1">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Admin Navigation Tabs */}
          <div className="flex flex-wrap gap-2">
            {adminLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.to;
              
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  title={link.description}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Page Content */}
        {children}
      </div>
    </div>
  );
};